import { useState } from 'react';
import { AlertTriangle, Check, Clock, Pencil, PhoneMissed, Star, UserCheck, Voicemail, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useMutation } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { Modal } from '@/components/ui/Modal';
import { useCrm } from './CrmContext';
import { CallButton } from './CallButton';
import { TaskEditor } from './TaskEditor';
import { buttonClass, errorMessage, formatDue, formatMinutes, formatWhen, inputClass } from './format';
import type { CrmTask } from './types';

/** One callback or follow-up with its actions: call, done, dismiss, take, edit. */
export function TaskItem({ task, showSubject = true }: { task: CrmTask; showSubject?: boolean }) {
  const { data, openContact, openCompany } = useCrm();
  const setStatus = useMutation(backendApi.crm.setTaskStatus);
  const assign = useMutation(backendApi.crm.assignTask);
  const reschedule = useMutation(backendApi.crm.rescheduleTask);
  const [editing, setEditing] = useState(false);
  const [closing, setClosing] = useState<'done' | 'dismissed' | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>, message?: string) => {
    setBusy(true);
    try {
      await fn();
      if (message) toast.success(message);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  const open = task.status === 'open';
  const isMine = Boolean(data?.prefs.extension && task.assigneeExtension === data.prefs.extension);

  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 p-3 rounded-lg border ${task.overdue ? 'border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-900/10' : 'border-gray-200 dark:border-gray-700'}`}>
      <div className="min-w-[16rem] flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          {task.kind === 'callback' ? (
            task.voicemail ? <Voicemail className="w-4 h-4 text-purple-600" /> : <PhoneMissed className="w-4 h-4 text-red-600" />
          ) : (
            <Clock className="w-4 h-4 text-primary-600" />
          )}
          {task.vip && <Star className="w-4 h-4 text-amber-500 fill-amber-400" aria-label="VIP" />}
          {showSubject && task.contactId ? (
            <button type="button" onClick={() => openContact(task.contactId!)} className="font-medium text-gray-900 dark:text-white hover:underline text-left">
              {task.title}
            </button>
          ) : showSubject && task.companyId ? (
            <button type="button" onClick={() => openCompany(task.companyId!)} className="font-medium text-gray-900 dark:text-white hover:underline text-left">
              {task.title}
            </button>
          ) : (
            <span className="font-medium text-gray-900 dark:text-white">{task.title}</span>
          )}
          {task.priority === 'high' && <span className="px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-[11px] font-medium">High</span>}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
          {task.kind === 'callback' && (
            <span>
              {task.missedCount && task.missedCount > 1 ? `${task.missedCount} missed calls, last ` : 'Missed '}
              {formatWhen(task.lastMissedAtMs)}
              {task.voicemail ? ' · left voicemail' : ''}
            </span>
          )}
          {task.attemptCount > 0 && open && <span>{task.attemptCount} callback attempt{task.attemptCount > 1 ? 's' : ''} not answered</span>}
          {open && task.dueAtMs && (
            <span className={task.overdue ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
              {task.overdue && <AlertTriangle className="inline w-3 h-3 mr-0.5 -mt-0.5" />}
              {formatDue(task.dueAtMs)}
            </span>
          )}
          <span>{task.assigneeExtension ? `→ ${task.assigneeName ?? task.assigneeExtension}${isMine ? ' (you)' : ''}` : 'Unassigned'}</span>
          {!open && (
            <span>
              {task.resolution === 'called_back'
                ? `Called back by ${task.resolvedBy ?? 'staff'}`
                : task.resolution === 'caller_reached'
                  ? `They called again and got ${task.resolvedBy ?? 'through'}`
                  : `${task.status === 'done' ? 'Done' : 'Dismissed'} by ${task.resolvedBy ?? 'staff'}`}
              {task.responseSeconds !== undefined && task.kind === 'callback' ? ` after ${formatMinutes(Math.round(task.responseSeconds / 60))}` : ''}
            </span>
          )}
        </p>
        {task.details && <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">{task.details}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {open && task.phone && <CallButton number={task.phone} size="sm" label="Call" />}
        {data?.canManage && open && (
          <>
            {!isMine && (
              <button type="button" disabled={busy} onClick={() => void run(() => assign({ taskId: task.id }), 'Assigned to you')} className={buttonClass.small} title="Assign to me">
                <UserCheck className="w-3.5 h-3.5" /> Take
              </button>
            )}
            <button type="button" disabled={busy} onClick={() => setClosing('done')} className={buttonClass.small}>
              <Check className="w-3.5 h-3.5" /> Done
            </button>
            <button type="button" disabled={busy} onClick={() => setClosing('dismissed')} className={buttonClass.small} title="Not needed">
              <X className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={() => setEditing(true)} className={buttonClass.small} title="Edit, reschedule or assign">
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button type="button" disabled={busy} onClick={() => {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              tomorrow.setHours(9, 0, 0, 0);
              void run(() => reschedule({ taskId: task.id, dueAtMs: tomorrow.getTime() }), 'Rescheduled to tomorrow at 9:00');
            }} className={buttonClass.small}>Tomorrow</button>
          </>
        )}
        {data?.canManage && !open && (
          <button type="button" disabled={busy} onClick={() => void run(() => setStatus({ taskId: task.id, status: 'open' }))} className={buttonClass.small}>
            Reopen
          </button>
        )}
      </div>
      {editing && <TaskEditor task={task} onClose={() => setEditing(false)} />}
      {closing && <Modal isOpen onClose={() => { setClosing(null); setNote(''); }} title={closing === 'done' ? 'Complete task' : 'Dismiss task'}>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">{task.title}</p>
        {(task.contactId || task.companyId) && <label className="block text-sm text-gray-700 dark:text-gray-200 mb-4">Outcome (optional; saved to the timeline)
          <textarea value={note} onChange={(event) => setNote(event.target.value)} rows={3} maxLength={4000} className={`${inputClass} mt-1`} />
        </label>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => { setClosing(null); setNote(''); }} className={buttonClass.secondary}>Cancel</button>
          <button type="button" disabled={busy} className={buttonClass.primary} onClick={() => void run(async () => {
            await setStatus({ taskId: task.id, status: closing, note: note.trim() || undefined });
            setClosing(null);
            setNote('');
          }, closing === 'done' ? 'Marked done' : 'Dismissed')}>{closing === 'done' ? 'Complete' : 'Dismiss'}</button>
        </div>
      </Modal>}
    </div>
  );
}
